'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import ResolveReferences from 'reference-resolvers/ResolveReferences';
import { isError, isLoading, isResolved } from 'reference-resolvers/utils';
import UILink from 'UIComponents/link/UILink';
import ReferenceIdType from 'reference-resolvers/schema/ReferenceIdType';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import SkeletonText from './SkeletonText';
import UnknownName from './UnknownName';

const getLoading = () => /*#__PURE__*/_jsx(SkeletonText, {});

const getError = ({
  referenceObjectType,
  id
}) => id || /*#__PURE__*/_jsx(UnknownName, {
  referenceObjectType: referenceObjectType
});

const getResolved = ({
  reference
}) => reference.label || '--';

const getUnknown = ({
  referenceObjectType
}) => /*#__PURE__*/_jsx(UnknownName, {
  referenceObjectType: referenceObjectType
});

const stopPropagation = e => {
  e.stopPropagation();
};
/**
 * Example:
 *   <ReferenceName id={123} referenceObjectType={ReferenceObjectTypes.DEAL} />
 * Renders as:
 *   My Deal Name
 *
 * Content can be customized via the 'on<status>' handlers:
 *   <ReferenceName
 *     id={123}
 *     referenceObjectType={ReferenceObjectTypes.DEAL}
 *     onLoading={({ reference, referenceObjectType, id }) => 'Loading...'}
 *     onError={({ reference, referenceObjectType, id }) => `Error loading ${id} :(`d}
 *     onResolved={({ reference, referenceObjectType, id }) => `${reference.label} is done!`}
 *     onUnknown={({ reference, referenceObjectType, id }) => `${referenceObjectType} id missing, deleted, or does not exist`}
 *   />
 *
 * Linking is supported via 'href':
 *   <ReferenceName
 *     href={'/path/to/object'}
 *   />
 *
 * Wrapping resolved reference in additional copy via 'messageKey':
 *  // list.byName: "by {{reference}}"
 *  <ReferenceName
 *    messageKey={'list.byName'}
 *  />
 * The supplied translation must include `{{reference}}`
 */


export const ReferenceNameView = ({
  reference,
  referenceObjectType,
  id,
  onLoading = getLoading,
  onError = getError,
  onResolved = getResolved,
  onUnknown = getUnknown,
  href,
  messageKey,
  className
}) => {
  let content = null;

  if (isLoading(reference)) {
    content = onLoading({
      reference,
      referenceObjectType,
      id
    });
  } else if (isError(reference)) {
    content = onError({
      reference,
      referenceObjectType,
      id
    });
  } else if (isResolved(reference)) {
    content = onResolved({
      reference,
      referenceObjectType,
      id
    });
  } else {
    // Don't link to unknown objects (does not exist or deleted).
    // Links will only take users to a 404.
    content = onUnknown({
      reference,
      referenceObjectType,
      id
    });
    return /*#__PURE__*/_jsx("span", {
      className: className,
      children: content
    });
  }

  content = href ? /*#__PURE__*/_jsx(UILink, {
    href: href,
    external: true,
    onClick: stopPropagation,
    className: className,
    children: content
  }) : /*#__PURE__*/_jsx("span", {
    className: className,
    children: content
  });
  content = messageKey ? /*#__PURE__*/_jsx(FormattedReactMessage, {
    message: messageKey,
    options: {
      reference: content
    }
  }) : content;
  return content;
};

const mapResolversToProps = (resolvers, props) => {
  return {
    reference: resolvers[props.referenceObjectType].byId(props.id)
  };
};

const ReferenceName = ResolveReferences(mapResolversToProps)(ReferenceNameView);
ReferenceName.propTypes = {
  referenceObjectType: PropTypes.string.isRequired,
  id: ReferenceIdType.isRequired,
  onLoading: PropTypes.func,
  onError: PropTypes.func,
  onResolved: PropTypes.func,
  onUnknown: PropTypes.func,
  href: PropTypes.string,
  messageKey: PropTypes.string,
  className: PropTypes.string
};
export default ReferenceName;